import { hotelCodeBatch, isHotelCodeImport, importCodes, importPollingInterval } from './onboarding';
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Alert, Button, Checkbox, FormControlLabel, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { client } from '@frontend/features/common/services';
import { catalogAdminKey } from './queries';
import { CodeCsvInput } from './CodeCsvInput';

export interface ImportJob {
  job_id: string;
  connector_account_id: string;
  connector: 'travelgate' | 'hotelbeds';
  access?: string;
  content_kind: string;
  status: string;
  attempt_count: number;
  max_attempts: number;
  pages_processed: number;
  has_checkpoint: boolean;
  last_error_code: string | null;
  criteria: Record<string, unknown>;
}
export function ImportJobs({
  codesOnly = false,
  onReview,
  onBusyChange,
}: {
  codesOnly?: boolean;
  onReview?(job: ImportJob): void;
  onBusyChange?(busy: boolean): void;
}) {
  const { t } = useTranslation();
  const [connector, setConnector] = useState<'travelgate' | 'hotelbeds'>('travelgate');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    onBusyChange?.(busy);
    return () => onBusyChange?.(false);
  }, [busy, onBusyChange]);
  return (
    <>
      {codesOnly && (
        <TextField
          select
          label={t('hotelCodeImport.connector')}
          value={connector}
          disabled={busy}
          onChange={(event) => setConnector(event.target.value as 'travelgate' | 'hotelbeds')}
        >
          <MenuItem value="travelgate">{t('hotelCodeImport.intermediary')}</MenuItem>
          <MenuItem value="hotelbeds">{t('hotelCodeImport.direct')}</MenuItem>
        </TextField>
      )}
      <ImportJobForm
        key={connector}
        connector={connector}
        codesOnly={codesOnly}
        onReview={onReview}
        onBusyChange={setBusy}
      />
    </>
  );
}
function ImportJobForm({
  connector,
  codesOnly,
  onReview,
  onBusyChange,
}: {
  connector: 'travelgate' | 'hotelbeds';
  codesOnly: boolean;
  onReview?(job: ImportJob): void;
  onBusyChange(busy: boolean): void;
}) {
  const { t } = useTranslation();
  const direct = connector === 'hotelbeds';
  const [csvPending, setCsvPending] = useState(false);
  const cache = useQueryClient();
  const [account, setAccount] = useState(''),
    [access, setAccess] = useState(''),
    [kind, setKind] = useState('supplier_hotel_list');
  const [countries, setCountries] = useState(''),
    [destinations, setDestinations] = useState(''),
    [hotels, setHotels] = useState('');
  const [confirmed, setConfirmed] = useState(false),
    [jobId, setJobId] = useState(''),
    [followingSince, setFollowingSince] = useState(0),
    [offset, setOffset] = useState(0);
  const accounts = useQuery(
    [...catalogAdminKey, 'import-accounts', connector],
    async () =>
      (
        await client.get<{
          data: Array<{
            connector_account_id: string;
            display_name: string;
            status: string;
            access_mode: string;
            access_codes: string[];
          }>;
        }>('hotel-catalog-admin/import-accounts', { params: { connector } })
      ).data.data,
    { retry: false },
  );
  const page = useQuery(
    [...catalogAdminKey, 'import-jobs', connector, offset],
    async () =>
      (
        await client.get<{ data: ImportJob[]; meta: { count: number } }>('hotel-catalog-admin/import-jobs', {
          params: { connector, limit: 25, offset },
        })
      ).data,
    { retry: false },
  );
  const job = useQuery(
    [...catalogAdminKey, 'import-job', jobId],
    async () =>
      (await client.get<{ data: ImportJob }>(`hotel-catalog-admin/import-jobs/${encodeURIComponent(jobId)}`)).data.data,
    {
      enabled: !!jobId,
      retry: false,
      refetchInterval: (data) => importPollingInterval(data?.status, followingSince),
      refetchIntervalInBackground: false,
    },
  );
  const inFlight = useRef(false);
  const batch = hotelCodeBatch(hotels, direct);
  const chosen = accounts.data?.find((a) => a.connector_account_id === account);
  const validAccount =
    chosen?.status === 'active' &&
    (direct
      ? chosen.access_mode === 'direct'
      : chosen.access_mode === 'include' && chosen.access_codes.includes(access));
  const follow = (id: string) => {
    setJobId(id);
    setFollowingSince(Date.now());
  };
  const save = useMutation(
    async () => {
      if (codesOnly && (!batch.valid || !confirmed || !validAccount || csvPending)) throw new Error('Invalid code import');
      const criteria = {
        countries: importCodes(countries),
        destination_codes: importCodes(destinations),
        hotel_codes: codesOnly ? batch.codes : importCodes(hotels),
        category_codes: [],
        group: null,
      };
      return (
        await client.post<{ data: ImportJob }>('hotel-catalog-admin/import-jobs', {
          connector_account_id: account,
          ...(!direct ? { access } : {}),
          content_kind: kind,
          criteria,
          max_size: 100,
          confirm_scope: confirmed,
        })
      ).data.data;
    },
    {
      retry: false,
      onSettled: () => {
        inFlight.current = false;
      },
      onSuccess: (data) => {
        follow(data.job_id);
        setConfirmed(false);
        cache.invalidateQueries([...catalogAdminKey, 'import-jobs']);
      },
    },
  );
  useEffect(() => {
    onBusyChange(save.isLoading);
    return () => onBusyChange(false);
  }, [save.isLoading, onBusyChange]);
  const change = () => {
    setConfirmed(false);
    save.reset();
  };
  const hotelFeed = ['hotel_master', 'supplier_hotel_list'].includes(kind);
  const bounded =
    !hotelFeed || importCodes(countries).length + importCodes(destinations).length + importCodes(hotels).length > 0;
  return (
    <Stack spacing={2} sx={{ my: 3 }}>
      <Typography variant="h5">{t(codesOnly ? 'hotelCodeImport.title' : 'hotelOnboarding.imports')}</Typography>
      <Alert severity="info">
        {t(
          codesOnly ? (direct ? 'hotelCodeImport.directHelp' : 'hotelCodeImport.help') : 'hotelOnboarding.importNotice',
        )}
      </Alert>
      {accounts.isError && <Alert severity="error">{t('hotelOnboarding.loadError')}</Alert>}
      <TextField
        select
        label={t('hotelOnboarding.account')}
        value={account}
        disabled={save.isLoading}
        onChange={(e) => {
          setAccount(e.target.value);
          setAccess('');
          change();
        }}
      >
        {(accounts.data ?? [])
          .filter((a) => a.status === 'active' && a.access_mode === (direct ? 'direct' : 'include'))
          .map((a) => (
            <MenuItem key={a.connector_account_id} value={a.connector_account_id}>
              {a.display_name} · {a.connector_account_id}
            </MenuItem>
          ))}
      </TextField>
      {!direct && (
        <TextField
          select
          label={t('hotelOnboarding.access')}
          value={access}
          disabled={save.isLoading}
          onChange={(e) => {
            setAccess(e.target.value);
            change();
          }}
        >
          {(chosen?.access_codes ?? []).map((a) => (
            <MenuItem key={a} value={a}>
              {a}
            </MenuItem>
          ))}
        </TextField>
      )}
      {!codesOnly && (
        <TextField
          select
          label={t('hotelOnboarding.feed')}
          value={kind}
          disabled={save.isLoading}
          onChange={(e) => {
            setKind(e.target.value);
            setCountries('');
            setDestinations('');
            setHotels('');
            change();
          }}
        >
          {['supplier_hotel_list', 'hotel_master', 'destination_list', 'board_list', 'category_list'].map((k) => (
            <MenuItem key={k} value={k}>
              {t(`hotelOnboarding.${k}`)}
            </MenuItem>
          ))}
        </TextField>
      )}
      {!codesOnly && hotelFeed && (
        <TextField
          label={t('hotelOnboarding.countries')}
          value={countries}
          disabled={save.isLoading}
          inputProps={{ maxLength: 100 }}
          onChange={(e) => {
            setCountries(e.target.value.toUpperCase());
            change();
          }}
        />
      )}
      {!codesOnly && (hotelFeed || kind === 'destination_list') && (
        <TextField
          label={t('hotelOnboarding.destinationCodes')}
          value={destinations}
          disabled={save.isLoading}
          multiline
          inputProps={{ maxLength: 20000 }}
          onChange={(e) => {
            setDestinations(e.target.value);
            change();
          }}
        />
      )}
      {codesOnly && (
        <CodeCsvInput
          key={hotels}
          direct={direct}
          disabled={save.isLoading}
          onStart={change}
          onPendingChange={setCsvPending}
          onApply={(codes) => {
            setHotels(codes);
            change();
          }}
        />
      )}
      {hotelFeed && (
        <TextField
          label={t(codesOnly ? 'hotelCodeImport.codes' : 'hotelOnboarding.hotelCodes')}
          helperText={codesOnly ? t(direct ? 'hotelCodeImport.directCodeHelp' : 'hotelCodeImport.codeHelp') : undefined}
          sx={{ '& textarea': { fontSize: 16 } }}
          value={hotels}
          disabled={save.isLoading}
          multiline
          inputProps={{ maxLength: 40000 }}
          onChange={(e) => {
            setHotels(e.target.value);
            change();
          }}
        />
      )}
      {codesOnly && hotels && (
        <Alert severity={batch.valid ? 'info' : 'warning'}>
          {t(
            batch.valid
              ? 'hotelCodeImport.count'
              : direct
                ? 'hotelCodeImport.directInvalid'
                : 'hotelCodeImport.invalid',
            {
              count: batch.codes.length,
              duplicates: batch.duplicates,
            },
          )}
        </Alert>
      )}
      <Typography>
        {t('hotelOnboarding.scope')}: {t(direct ? 'hotelCodeImport.direct' : 'hotelCodeImport.intermediary')} ·{' '}
        {account}
        {!direct && ` / ${access}`} · {kind} · {countries || '—'} / {destinations || '—'} /{' '}
        {(codesOnly ? batch.codes.join(', ') : hotels) || '—'}
      </Typography>
      <FormControlLabel
        control={
          <Checkbox checked={confirmed} disabled={save.isLoading || csvPending} onChange={(_, v) => setConfirmed(v)} />
        }
        label={t('hotelOnboarding.confirmScope')}
      />
      <Button
        disabled={
          csvPending ||
          !confirmed ||
          !account ||
          (!direct && !access) ||
          !bounded ||
          save.isLoading ||
          accounts.isError ||
          (codesOnly && (!batch.valid || !validAccount))
        }
        onClick={() => {
          if (inFlight.current) return;
          inFlight.current = true;
          save.mutate();
        }}
      >
        {t('hotelOnboarding.enqueue')}
      </Button>
      {save.isError && (
        <Alert severity="error">{t(codesOnly ? 'hotelCodeImport.enqueueError' : 'hotelOnboarding.enqueueError')}</Alert>
      )}
      {job.data && (
        <Alert severity={job.data.status === 'terminal_failed' ? 'warning' : 'info'}>
          {job.data.job_id} · {job.data.status} · {t('hotelOnboarding.pages')}: {job.data.pages_processed} ·{' '}
          {t('hotelOnboarding.attempts')}: {job.data.attempt_count}/{job.data.max_attempts}{' '}
          {job.data.has_checkpoint && t('hotelOnboarding.checkpoint')} {job.data.last_error_code}
        </Alert>
      )}
      {codesOnly &&
        job.data &&
        isHotelCodeImport(job.data) &&
        ['completed', 'terminal_failed'].includes(job.data.status) && (
        <Button
          disabled={save.isLoading}
          onClick={() => {
            if (!save.isLoading) onReview?.(job.data!);
          }}
        >
          {t('hotelCodeImport.review')}
        </Button>
      )}
      {job.isError && <Alert severity="error">{t('hotelOnboarding.loadError')}</Alert>}
      {!!jobId && (
        <Button
          onClick={() => {
            setFollowingSince(Date.now());
            job.refetch();
          }}
        >
          {t('hotelOnboarding.refreshStatus')}
        </Button>
      )}
      <Alert severity="info">{t('hotelOnboarding.resumeNotice')}</Alert>
      <Typography variant="h6">{t('hotelOnboarding.existingJobs')}</Typography>
      {page.isError && <Alert severity="error">{t('hotelOnboarding.loadError')}</Alert>}
      {(page.data?.data ?? [])
        .filter((j) => !codesOnly || isHotelCodeImport(j))
        .map((j) => (
          <Button key={j.job_id} onClick={() => follow(j.job_id)}>
            {j.job_id} · {j.content_kind} · {j.access ?? t('hotelCodeImport.direct')} · {j.status}
            {codesOnly && ` · ${j.connector_account_id} · ${(j.criteria.hotel_codes as string[]).join(', ')}`}
          </Button>
        ))}
      <Stack direction="row">
        <Button disabled={!offset || page.isFetching} onClick={() => setOffset((v) => Math.max(0, v - 25))}>
          {t('hotelCatalogAdmin.previous')}
        </Button>
        <Button
          disabled={page.isFetching || (page.data?.meta.count ?? 0) < 25 || offset >= 10000}
          onClick={() => setOffset((v) => v + 25)}
        >
          {t('hotelCatalogAdmin.next')}
        </Button>
        <Button onClick={() => page.refetch()}>{t('hotelCatalogAdmin.reload')}</Button>
      </Stack>
    </Stack>
  );
}
